import dayjs from 'dayjs'
import { CaseCard, toCaseCardImage } from '@/components/parts/case-card'
import type { ChipSurface } from '@/components/parts/chip'
import type { Seminar } from '@/types/microcms/seminar'

type SeminarListSectionProps = {
  seminars: Seminar[]
}

/**
 * 開催日時が now より後なら受付中（旧 SeminarList の判定を踏襲）。詳細ページでも使う。
 * 時刻の比較なのでタイムゾーンは結果に影響しない。
 * クライアントコンポーネントでは hydration がずれないよう、サーバーと同じ now を渡すこと
 */
export const isSeminarOpen = (seminar: Seminar, now: number = Date.now()) =>
  seminar.eventdate ? dayjs(seminar.eventdate).isAfter(now) : false

/** microCMS のタイトルに入っている改行タグを外す */
export const toSeminarTitle = (seminar: Seminar) =>
  seminar.title?.replace(/<br>|<br \/>/g, '') ?? ''

/**
 * カードに載せる受付状態のタグ。受付終了は灰地にする。
 * 受付中の面色はカードを置く面に合わせる（白い面ならベージュ、生成りの面なら白）
 */
export const toSeminarStatusTag = (
  seminar: Seminar,
  openSurface: ChipSurface = 'beige',
  now?: number
): { group: string; label: string; surface: ChipSurface } =>
  isSeminarOpen(seminar, now)
    ? { group: 'status', label: 'お申込み受付中', surface: openSurface }
    : { group: 'status', label: '受付終了', surface: 'muted' }

/** 開催日の新しい順。開催日が無いものは末尾に回す */
const sortByEventDate = (seminars: Seminar[]) =>
  [...seminars].sort((a, b) => {
    const dateA = a.eventdate ? dayjs(a.eventdate) : dayjs(0)
    const dateB = b.eventdate ? dayjs(b.eventdate) : dayjs(0)
    return dateB.diff(dateA)
  })

/**
 * セミナー一覧。Figma: （PC）セミナー の sec/一覧。
 * 見出しは持たず、CaseCard（タイトル 3 行省略＋受付状態のタグ）を 3 列で並べる。
 */
export function SeminarListSection({ seminars }: SeminarListSectionProps) {
  const sorted = sortByEventDate(seminars)

  return (
    <section className="bg-surface px-6 py-12 lg:px-panelx lg:py-panelx">
      <div className="mx-auto max-w-wide">
        {sorted.length > 0 ? (
          <ul className="grid w-full grid-cols-1 gap-x-10 gap-y-10 md:grid-cols-2 lg:grid-cols-3 lg:gap-y-14">
            {sorted.map((seminar) => (
              <CaseCard
                key={seminar.id}
                href={`/seminar/${seminar.id}/`}
                title={toSeminarTitle(seminar)}
                clampTitle
                image={toCaseCardImage(seminar.eyecatch)}
                tags={[toSeminarStatusTag(seminar)]}
              />
            ))}
          </ul>
        ) : (
          <p className="text-center text-base leading-body text-ink-subtle">
            現在公開中のセミナーはありません。
          </p>
        )}
      </div>
    </section>
  )
}
